import { FC } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useUpdateRentingPlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useUpdateRentingPlanMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_MUTATION_KEY } from '@telialt/core-eshop/src/model';
import { Loader } from '@telialt/core-eshop/src/product/Loader';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { RentingPeriod } from '@telialt/models/src/EshopModel';
import { selectRentPlan } from '@telialt/utils/src/eshopSelectors';
import { withParams } from '@telialt/utils/src/helpers';

import {
    MonthlyPrice,
    OldPrice,
    Period,
    Radio,
    RadioLabel,
    RentPlanSelectorSection,
    ResidualPrice,
    ResidualTag,
    Title,
} from './RentPlanSelector.styles';
import { useInitializeRentPlan } from './useInitializeRentPlan';

export const RentPlanSelector: FC = () => {
    const { t } = useTranslations();
    const { cart, cartIsLoading } = useCartQuery();
    const { updateRentingPlan, updateRentingPlanIsLoading } = useUpdateRentingPlanMutation();
    const selectedRentingPlan = selectRentPlan(cart);
    const isChangingContractType = !!useIsMutating({ mutationKey: [ESHOP_MUTATION_KEY.UPDATE_CONTRACT_TYPE] });
    const isLoading = cartIsLoading || updateRentingPlanIsLoading || isChangingContractType;
    useInitializeRentPlan();

    if (!cart.possibleRentingPlans?.length) {
        return null;
    }

    return (
        <RentPlanSelectorSection data-test={ESHOP_TEST_ID.RENTING_PLAN_SELECTOR_FORM}>
            <Loader isLoading={isLoading} dataTest={ESHOP_TEST_ID.RENTING_PLAN_LOADER} />
            <Title tag="h3" variant="title-100">
                {t['eshop.product_page.renting.label']}
            </Title>
            {cart.possibleRentingPlans.map((o) => {
                const oldPrice = o.monthlyOldPrice;
                const rentingPeriod = o.monthValue;

                return (
                    <Radio
                        key={o.rentingPeriod}
                        value={o.rentingPeriod}
                        name="rent-selection"
                        fullWidth={true}
                        checked={selectedRentingPlan?.rentingPeriod === o.rentingPeriod}
                        label={
                            <RadioLabel>
                                <Period>{withParams(t['eshop.product_page.renting.period'], { rentingPeriod })}</Period>
                                <MonthlyPrice $highlighted={!!oldPrice}>{o.monthlyPrice.formattedValue}</MonthlyPrice>
                                <ResidualTag>{t['eshop.product_page.renting.residual_price']}</ResidualTag>
                                <ResidualPrice>{o.residualPrice?.formattedValue}</ResidualPrice>
                                <OldPrice hidden={!oldPrice}>{oldPrice?.formattedValue}</OldPrice>
                            </RadioLabel>
                        }
                        onChange={(event) => updateRentingPlan(event.target.value as RentingPeriod)}
                        data-test={ESHOP_TEST_ID.CHECKOUT_PAYMENT_METHOD}
                    />
                );
            })}
        </RentPlanSelectorSection>
    );
};
